import Spacer from "@/components/atoms/utilities/Spacer";
import { PageTitle } from "@/components/molecules";
import { MainTemplate } from "@/components/templates";
import React, { useContext, useEffect, useState } from "react";
import {
  unFormatDecimal,
  useSnackBar,
} from "@moneyforward/mfx_biz_react_components";
import {
  BankBranchOffice,
  Company,
  EditCompanyInfoInput,
  EditCompanyInfoMutation,
  useEditCompanyInfoMutation,
  useGetBankBranchOfficesLazyQuery,
  useGetCompanyInfoLazyQuery,
} from "@/generated/graphql";
import { AuthContext, AuthStatus } from "@/contexts/AuthContext";
import { FormProvider, useForm } from "react-hook-form";
import {
  RegisterCompanyInfoForm,
  RegisterCompanyInfoFormValues,
} from "@/components/organisms/RegisterCompanyInfoForm";
import { useRouter } from "next/router";
import { useCompanyLazyQuery } from "@/generated/graphql_billing";
import config from "@/libs/config";
import useRollbarUtil from "../hooks/useRollbarUtil";

type Props = {};

export const EditCompanyPage = (props: Props) => {
  const { getNotifyErrorHandlerForGraphQL } = useRollbarUtil();
  const router = useRouter();
  const { authStatus } = useContext(AuthContext);
  const [company, setCompany] = useState<Company>();
  const [bankBranchOffices, setBankBranchOffices] =
    useState<BankBranchOffice[]>();
  const [isContractAdmin, setIsContractAdmin] = useState(false);
  const { notify } = useSnackBar();

  const methods = useForm<RegisterCompanyInfoFormValues>({
    reValidateMode: "onBlur",
    defaultValues: {},
  });

  const { userInfo } = useContext(AuthContext);

  const [getCompanyInfoQuery, { loading: getCompanyInfoLoading }] =
    useGetCompanyInfoLazyQuery({
      onCompleted: (data) => {
        if (data?.company) {
          setCompany(data?.company as Company);
        }
      },
      onError: getNotifyErrorHandlerForGraphQL("Failed to get company info"),
    });

  const [getBankBranchOfficesQuery, { loading: getBankBranchOfficesLoading }] =
    useGetBankBranchOfficesLazyQuery({
      onCompleted: (data) => {
        setBankBranchOffices(data.bankBranchOffices);
      },
      onError: getNotifyErrorHandlerForGraphQL(
        "Failed to get bank branch offices"
      ),
    });

  const [getBillingCompanyQuery, { loading: getBillingCompanyLoading }] =
    useCompanyLazyQuery({
      onCompleted: (data) => {
        if (data) {
          setIsContractAdmin(
            !!data.company.admins.find(
              (admin) => admin.userSub === userInfo.sub
            )
          );
        }
      },
      onError: getNotifyErrorHandlerForGraphQL("Failed to get billing company"),
      context: {
        api: "billing",
      },
      variables: {
        uuid: userInfo.xCompanyID as never,
      },
    });

  useEffect(() => {
    if (authStatus === AuthStatus.SignedIn) {
      getCompanyInfoQuery();
      config.IS_INDUSTRY_TYPE_BANK && getBankBranchOfficesQuery();
      getBillingCompanyQuery();
    }
  }, [authStatus]);

  useEffect(() => {
    if (company) {
      const form: RegisterCompanyInfoFormValues = {
        companyBusinessType: company.companyBusinessType,
        blockBuildingName: company.blockBuildingName,
        branchOfficeId: company.branchOfficeId,
        businessContent: company.businessContent,
        capital: company.capital && company.capital / 10000,
        companyNameStoreNameKana: company.companyNameStoreNameKana,
        corporateNumber: company.corporateNumber,
        qualifiedInvoiceIssuerRegistrationNumber:
          company.qualifiedInvoiceIssuerRegistrationNumber &&
          company.qualifiedInvoiceIssuerRegistrationNumber.replace(/^T/, ""),
        qualifiedInvoiceIssuerRegistrationNumberCheck:
          company.qualifiedInvoiceIssuerRegistrationNumber && true,
        foundationDate:
          company.foundationDate && company.foundationDate.replace(/-/g, "/"),
        industriesLarge: company.industriesLarge,
        industriesMiddle: company.industriesMiddle,
        municipality: company.municipality,
        phoneNumber: company.phoneNumber,
        postalCodeEnd: company.postalCode?.substring(3, 7),
        postalCodeForward: company.postalCode?.substring(0, 3),
        prefectures: company.prefectures,
        representativeEmailAddress: company.representativeEmailAddress,
        representativeBirthDate:
          company.representativeBirthDate &&
          company.representativeBirthDate.replace(/-/g, "/"),
        representativeName: company.representativeName,
        settlementMonth: company.settlementMonth,
        siteUrl: company.siteUrl,
        staffCount: company.staffCount,
        mainUserName: company.mainUserName,
        mainUserPhoneNumber: company.mainUserPhoneNumber,
        companyNameStoreName: company.companyNameStoreName,
        insuranceAgencyCode: company.insuranceAgencyCode,
        insuranceAgencyName: company.insuranceAgencyName,
      };
      methods.reset(form);
    }
  }, [company]);

  const [editCompanyInfoMutation] = useEditCompanyInfoMutation({
    onCompleted: (data: EditCompanyInfoMutation) => {
      notify("変更が完了しました");
      router.push("/manage-company");
    },
    onError: getNotifyErrorHandlerForGraphQL(
      "Failed to edit company info",
      "変更に失敗しました"
    ),
  });

  const handleSubmit = async (formValues: RegisterCompanyInfoFormValues) => {
    let input: EditCompanyInfoInput = {
      companyBusinessType: formValues.companyBusinessType,
      blockBuildingName: formValues.blockBuildingName
        ? formValues.blockBuildingName
        : null,
      branchOfficeId: formValues.branchOfficeId,
      businessContent: formValues.businessContent
        ? formValues.businessContent
        : null,
      capital: formValues.capital
        ? parseFloat(unFormatDecimal(formValues.capital)) * 10000
        : null,
      companyNameStoreName: formValues.companyNameStoreName,
      companyNameStoreNameKana: formValues.companyNameStoreNameKana
        ? formValues.companyNameStoreNameKana
        : null,
      corporateNumber: formValues.corporateNumber
        ? formValues.corporateNumber
        : null,
      qualifiedInvoiceIssuerRegistrationNumber:
        formValues.qualifiedInvoiceIssuerRegistrationNumberCheck &&
        formValues.qualifiedInvoiceIssuerRegistrationNumber
          ? "T" + formValues.qualifiedInvoiceIssuerRegistrationNumber
          : null,
      foundationDate: formValues.foundationDate
        ? formValues.foundationDate.replace(/\//g, "-")
        : null,
      industriesLarge: formValues.industriesLarge,
      industriesMiddle: formValues.industriesMiddle
        ? formValues.industriesMiddle
        : null,
      municipality: formValues.municipality ? formValues.municipality : null,
      phoneNumber: formValues.phoneNumber,
      postalCode:
        formValues.postalCodeForward && formValues.postalCodeEnd
          ? formValues.postalCodeForward + formValues.postalCodeEnd
          : null,
      prefectures: formValues.prefectures ? formValues.prefectures : null,
      representativeName: formValues.representativeName,
      representativeBirthDate: formValues.representativeBirthDate
        ? formValues.representativeBirthDate.replace(/\//g, "-")
        : null,
      representativeEmailAddress: formValues.representativeEmailAddress
        ? formValues.representativeEmailAddress
        : null,
      settlementMonth: formValues.settlementMonth,
      staffCount: formValues.staffCount ? formValues.staffCount : null,
      siteUrl: formValues.siteUrl ? formValues.siteUrl : null,
      mainUserName: formValues.mainUserName,
      mainUserPhoneNumber: formValues.mainUserPhoneNumber,
    };

    if (config.IS_INDUSTRY_TYPE_INSURANCE) {
      input = {
        ...input,
        insuranceAgencyCode: formValues.insuranceAgencyCode,
        insuranceAgencyName: formValues.insuranceAgencyName,
      };
    }

    await editCompanyInfoMutation({
      variables: { input },
    });
  };

  return (
    <MainTemplate title={`事業者情報の編集`}>
      {/*See comment in MainTemplate.tsx - contentInner*/}
      {/*<Spacer size={56} />*/}
      <PageTitle mainTitle={`事業者情報の編集`} />
      {getCompanyInfoLoading ||
      getBankBranchOfficesLoading ||
      getBillingCompanyLoading ||
      (config.IS_INDUSTRY_TYPE_BANK && !bankBranchOffices) ? (
        <p>loading...</p>
      ) : (
        <div>
          <FormProvider {...methods}>
            <RegisterCompanyInfoForm
              onSubmit={handleSubmit}
              bankBranchOffices={bankBranchOffices}
              isEdit={true}
              isContractAdmin={isContractAdmin}
            />
          </FormProvider>
        </div>
      )}
    </MainTemplate>
  );
};
